import isEmpty from 'lodash/isEmpty'
import React, {
  FC,
  useCallback,
  useContext,
  useRef,
} from 'react'
import { useLockBodyScroll } from 'react-use'
import styled from 'styled-components'

import CloseIcon from '@Assets/SVG/Close'
import SearchIcon from '@Assets/SVG/Search'
import { SVGIcon } from '@Components/Icons'
import { PreviousSearches } from '@Components/Search/PreviousSearches'
import { QueryResults } from '@Components/Search/QueryResults'
import { handleEvent } from '@Events/Manager'
import { NavigationEvent } from '@Events/Navigation'
import { MetadataContext } from '@Globals/Metadata'
import { SearchContext } from '@Globals/Search/Context'
import { addSearchFromFreeText } from '@Globals/Search/PreviousSearches'
import { down } from '@Globals/Style/breakpoints'
import { box, flexCenterChildren } from '@Globals/Style/mixins'
import appTheme from '@Globals/Style/theme'
import { useTranslation } from '@Globals/Translations'
import { useMediaQuery } from '@Globals/useMediaQuery'

const MainBody: FC = () => {
  const {
    previousSearches,
    query,
    results,
    selection,
  } = useContext(SearchContext)

  const hasQuery = query?.length >= 2
  const hasPreviousSearches = !isEmpty(previousSearches)

  if (hasQuery) {
    return <QueryResults query={query} results={results} selection={selection} />
  }
  if (hasPreviousSearches) {
    return <PreviousSearches previousSearches={previousSearches} selection={selection} />
  }
  return null
}

const StyledWrapper = styled.div`
  display: none;

  ${down('desktop')} {
    display: block;
    position: fixed;
    top: 0;
    left: 0;
    z-index: ${({ theme }) => theme.zIndexes.mobileHeaderSearch};
    width: 100vw;
    height: 100%;
    background-color: #fff;

    * {
      -webkit-tap-highlight-color: rgba(0, 0, 0, 0);
    }
  }
`

const StyledHeader = styled.div`
  display: flex;
  align-items: center;
  height: 60px;
  padding: 0 20px;
  border-bottom: 1px solid ${({ theme }) => theme.colors.greyLighter};

  ${down('phablet')} {
    padding: 0 15px;
  }
`

const StyledSearchIcon = styled.div`
  ${flexCenterChildren};
  flex-shrink: 0;
  margin-right: 10px;

  & > svg {
    fill: ${({ theme }) => theme.colors.greyDark}
  }
`

const StyledInput = styled.input`
  display: block;
  flex-grow: 2;
  height: 40px;
  border: none;
`

const StyledClearIcon = styled.div`
  ${box({ width: '40px' })}
  ${flexCenterChildren}
  flex-shrink: 0;
  padding: 15px;
  cursor: pointer;

  & > svg {
    fill: ${({ theme }) => theme.colors.grey};
  }
`

const StyledCancel = styled.div`
  height: 40px;
  ${flexCenterChildren};
  flex-shrink: 0;
  cursor: pointer;

  & > span {
    padding-top: 1px;
    text-transform: uppercase;
    font-weight: bold;
    font-size: 0.875rem;
    line-height: 1;
    color: ${({ theme }) => theme.colors.primary};
  }
`

export const MobileSearchResults: FC = () => {
  const { t } = useTranslation()
  const { storeURL } = useContext(MetadataContext)
  const searchURL = storeURL('catalogsearch/result')
  const inputRef = useRef<HTMLInputElement>(null)
  const isDesktop = useMediaQuery(`(min-width: ${appTheme.breakpoints.desktop})`)

  const {
    active,
    query,
    setActive,
    setQuery,
  } = useContext(SearchContext)

  useLockBodyScroll(active && !isDesktop)

  const enter = useCallback((e) => {
    if (e.key === 'Enter' || e.keyCode === 13) {
      if (query.length > 1) {
        const url = new URL(searchURL)
        url.searchParams.set('q', query)
        addSearchFromFreeText(query, searchURL.toString())
        setActive(false)
        handleEvent(
          new NavigationEvent({
            attributes: {
              dest_page_type: 'searchresults',
              dest_page_type_id: '',
              search_query: query,
              source_id: 'site_search_results',
            },
            eventName: 'nav_link_click',
          }),
          true,
        )
        window.location.href = url.toString()
      }
    }
  }, [query, searchURL, setActive])

  if (!active) {
    return null
  }

  return (
    <StyledWrapper>
      <StyledHeader>
        <StyledSearchIcon>
          <SVGIcon icon={SearchIcon} size="20px" />
        </StyledSearchIcon>
        <StyledInput
          autoFocus // eslint-disable-line jsx-a11y/no-autofocus
          onChange={e => setQuery(e.target.value || '')}
          onKeyDown={enter}
          placeholder={t('header:SEARCH_FIELD_PLACEHOLDER_TEXT')}
          ref={inputRef}
          value={query}
        />
        {query && (
          <StyledClearIcon
            onClick={(e) => {
              e.stopPropagation()
              if (inputRef?.current) {
                setQuery('')
                inputRef.current.value = ''
                inputRef.current.focus()
              }
            }}
          >
            <SVGIcon icon={CloseIcon} size="10px" />
          </StyledClearIcon>
        )}
        <StyledCancel onClick={() => setActive(false)}>
          <span>{t('header:CANCEL')}</span>
        </StyledCancel>
      </StyledHeader>
      <MainBody />
    </StyledWrapper>
  )
}
